import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Abogalia';
const DEFAULT_TITLE = 'Abogalia | Encontrá Abogados Online en Argentina - Consultá Gratis';
const DEFAULT_DESCRIPTION = 'Encontrá abogados verificados en Argentina. Publicá tu caso gratis y recibí propuestas en minutos. Abogado de familia, laboral, penal, civil, sucesiones y más. +1.000 abogados.';
const DEFAULT_IMAGE = '/abogalia-og.jpg';
const BASE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://abogalia.com.ar';

// Normalize URL by removing trailing slashes and query params (for canonical)
function normalizeCanonicalUrl(url: string): string {
  // Remove trailing slashes except for root
  const normalized = url.replace(/\/+$/, '') || '/';
  // Remove query parameters and fragments
  return normalized.split('?')[0].split('#')[0];
}

// Helper functions for Twitter Cards
function truncateTitle(title: string, maxLength: number = 70): string {
  if (title.length <= maxLength) return title;
  return title.substring(0, maxLength - 3) + '...';
}

function truncateDescription(description: string, maxLength: number = 200): string {
  if (description.length <= maxLength) return description;
  return description.substring(0, maxLength - 3) + '...';
}

type PageSEOProps = {
  title?: string;
  description?: string;
  image?: string;
  canonicalPath?: string;
  noindex?: boolean;
  lang?: string;
  type?: 'website' | 'article' | 'profile';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  keywords?: string[];
  structuredData?: Record<string, any> | Record<string, any>[];
};

export function DefaultSEO() {
  return (
    <Helmet>
      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>{DEFAULT_TITLE}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />
      <meta name="keywords" content="abogados argentina, abogado online, consulta legal, asesoramiento legal, encontrar abogado, abogados verificados, servicios legales, abogado civil, abogado laboral, abogado familia" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="theme-color" content="#0F172A" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={DEFAULT_TITLE} />
      <meta property="og:description" content={DEFAULT_DESCRIPTION} />
      <meta property="og:image" content={`${BASE_URL}/abogalia-og.jpg`} />
      <meta property="og:image:alt" content={`${SITE_NAME} - Logo`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={BASE_URL} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_AR" />
      <meta property="fb:app_id" content="2258893974592165" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={truncateTitle(DEFAULT_TITLE)} />
      <meta name="twitter:description" content={truncateDescription(DEFAULT_DESCRIPTION)} />
      <meta name="twitter:image" content={`${BASE_URL}${DEFAULT_IMAGE}`} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} - Logo`} />

      <link rel="icon" type="image/png" href="/icon.png" />
      <link rel="canonical" href={BASE_URL} />

      {/* i18n basic */}
      <meta httpEquiv="content-language" content="es-AR" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://abogalia.com.ar/#organization',
          name: SITE_NAME,
          url: BASE_URL,
          logo: {
            '@type': 'ImageObject',
            url: `${BASE_URL}${DEFAULT_IMAGE}`,
            width: 512,
            height: 512
          },
          description: DEFAULT_DESCRIPTION,
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'AR',
            addressLocality: 'Argentina'
          },
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            availableLanguage: ['Spanish', 'es']
          },
          sameAs: [
            'https://www.facebook.com/Abogalia.ia',
            'https://www.instagram.com/abogalia_ia/',
            'https://www.tiktok.com/@abogalia_ia',
            'https://x.com/AbogaliaOK'
          ]
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          '@id': 'https://abogalia.com.ar/#website',
          name: SITE_NAME,
          url: BASE_URL,
          description: DEFAULT_DESCRIPTION,
          inLanguage: 'es-AR',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${BASE_URL}/lawyer/directory?query={search_term_string}`
            },
            'query-input': 'required name=search_term_string',
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ProfessionalService',
          name: SITE_NAME,
          description: DEFAULT_DESCRIPTION,
          url: BASE_URL,
          priceRange: 'Gratis para clientes',
          areaServed: {
            '@type': 'Country',
            name: 'Argentina'
          },
          serviceType: ['Legal Services', 'Lawyer Directory', 'Legal Consultation']
        })}
      </script>
    </Helmet>
  );
}

export function PageSEO({ 
  title, 
  description, 
  image, 
  canonicalPath, 
  noindex, 
  lang = 'es-AR',
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  keywords = [],
  structuredData
}: PageSEOProps) {
  const location = useLocation();
  const rawPath = canonicalPath ?? location.pathname;
  const normalizedPath = normalizeCanonicalUrl(rawPath);
  const fullCanonical = `${BASE_URL}${normalizedPath}`;
  const pageTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const fullImage = `${BASE_URL}${image ?? DEFAULT_IMAGE}`;

  return (
    <Helmet>
      {title && <title>{pageTitle}</title>}
      {description && <meta name="description" content={description} />}
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      <link rel="canonical" href={fullCanonical} />
      <meta httpEquiv="content-language" content={lang} />

      {/* Open Graph per-page */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title ? pageTitle : DEFAULT_TITLE} />
      <meta property="og:description" content={description || DEFAULT_DESCRIPTION} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={image ? `${BASE_URL}${image}` : `${BASE_URL}/abogalia-og.jpg`} />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_AR" />
      <meta property="fb:app_id" content="2258893974592165" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={truncateTitle(title ? pageTitle : DEFAULT_TITLE)} />
      <meta name="twitter:description" content={truncateDescription(description || DEFAULT_DESCRIPTION)} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={title || SITE_NAME} />
      
      {/* Additional SEO meta tags */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(structuredData) ? structuredData : structuredData)}
        </script>
      )}
    </Helmet>
  );
}

export function RouteSEOWatcher() {
  const { pathname } = useLocation();
  const isPublicLawyerProfile = /^\/lawyer\/[^/]+\/profile$/.test(pathname);
  const isPrivate = (pathname.startsWith('/user') || pathname.startsWith('/lawyer')) && !isPublicLawyerProfile;
  return (
    <Helmet>
      {isPrivate ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
    </Helmet>
  );
}

export function StaticRouteSEO() {
  const { pathname } = useLocation();
  const metaMap: Record<string, { 
    title: string; 
    description: string;
    keywords?: string[];
  } | undefined> = {
    '/': {
      title: 'Encontrá Abogados Online en Argentina - Consultá Gratis',
      description:
        'Publicá tu caso gratis y recibí propuestas de abogados verificados en minutos. Abogado de familia, laboral, penal, civil, sucesiones e inmobiliario. +1.000 abogados en Argentina.',
      keywords: ['abogados argentina', 'abogado online', 'abogado', 'consulta legal', 'abogado de familia', 'abogado laboral', 'abogado penalista', 'abogado civil', 'abogados online', 'encontrar abogado'],
    },
    '/how-work': {
      title: 'Cómo funciona Abogalia para clientes',
      description:
        'Publicá tu caso legal gratis, recibí propuestas de abogados verificados y elegí al mejor. Sin intermediarios, sin comisiones. Así de simple.',
      keywords: ['cómo funciona', 'publicar caso legal', 'encontrar abogado online', 'consulta legal online gratis'],
    },
    '/how-work-lawyer': {
      title: 'Cómo funciona Abogalia para abogados',
      description:
        'Recibí casos verificados en tu especialidad, hacé propuestas y conseguí nuevos clientes. La plataforma de marketing legal más efectiva de Argentina.',
      keywords: ['abogados', 'conseguir clientes', 'estudio jurídico', 'casos legales', 'marketing legal', 'captar clientes abogado'],
    },
    '/lawyer/directory': {
      title: 'Directorio de abogados por especialidad y provincia',
      description:
        'Buscá abogados verificados por especialidad y provincia: civil, laboral, familia, penal, sucesiones, inmobiliario y más en toda Argentina.',
      keywords: ['directorio abogados', 'abogados por especialidad', 'abogados civiles', 'abogados laborales', 'abogados de familia', 'abogados penalistas'],
    },
    '/pricing': {
      title: 'Planes y precios para abogados',
      description:
        'Elegí el plan ideal para tu estudio jurídico: acceso a casos, visibilidad en directorio y herramientas de gestión. Empezá gratis.',
      keywords: ['planes abogados', 'precios', 'suscripción abogados', 'plan profesional', 'marketing abogados'],
    },
    '/faqs': {
      title: 'Preguntas frecuentes',
      description:
        'Respondemos las dudas más comunes sobre cómo usar Abogalia como cliente o abogado.',
      keywords: ['preguntas frecuentes', 'ayuda', 'faq', 'soporte'],
    },
    '/about-us': {
      title: 'Quiénes somos',
      description:
        'Conocé la misión de Abogalia: simplificar el acceso a servicios legales de calidad.',
      keywords: ['quiénes somos', 'sobre nosotros', 'misión', 'equipo'],
    },
    '/contact': {
      title: 'Contacto',
      description: 'Escribinos para soporte, prensa o alianzas.',
      keywords: ['contacto', 'soporte', 'email', 'ayuda'],
    },
    '/privacy-policy': {
      title: 'Política de privacidad',
      description: 'Cómo protegemos tus datos personales en Abogalia.',
      keywords: ['privacidad', 'datos personales', 'protección de datos', 'GDPR'],
    },
    '/tyc': {
      title: 'Términos y condiciones',
      description: 'Condiciones de uso de la plataforma Abogalia.',
      keywords: ['términos y condiciones', 'condiciones de uso', 'términos legales'],
    },
    '/cookies': {
      title: 'Política de cookies',
      description: 'Información sobre cookies utilizadas en Abogalia.',
      keywords: ['cookies', 'política de cookies', 'tracking'],
    },
  };

  const meta = metaMap[pathname];
  if (!meta) return null;
  return <PageSEO 
    title={meta.title} 
    description={meta.description} 
    canonicalPath={pathname}
    keywords={meta.keywords}
  />;
}

// Helper functions for common structured data types
export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

export const generateArticleSchema = (data: {
  headline: string;
  description: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: data.headline,
  description: data.description,
  image: data.image ? `${BASE_URL}${data.image}` : `${BASE_URL}${DEFAULT_IMAGE}`,
  datePublished: data.datePublished,
  dateModified: data.dateModified,
  author: {
    '@type': data.author ? 'Person' : 'Organization',
    name: data.author || SITE_NAME
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}${DEFAULT_IMAGE}`
    }
  }
});

// LegalService schema para páginas de especialidad-localidad (rich results de servicio).
// NOTA: no incluimos aggregateRating hasta tener reseñas reales visibles en la página;
// marcar ratings inventados viola las guías de Google y puede penalizar el sitio.
export const generateLegalServiceSchema = (data: {
  serviceName: string;
  description: string;
  areaServed: string;
  url: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'LegalService',
  name: data.serviceName,
  description: data.description,
  url: data.url,
  provider: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: BASE_URL,
  },
  areaServed: {
    '@type': 'City',
    name: data.areaServed,
  },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'ARS',
    description: 'Publicar tu caso es gratis. Solo acordás honorarios directamente con el abogado, sin comisiones.',
  },
});

export const generateLocalBusinessSchema = (data: {
  name: string;
  description: string;
  address: {
    streetAddress?: string;
    addressLocality: string;
    addressRegion?: string;
    postalCode?: string;
  };
  phone?: string;
  url?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': data.url || BASE_URL,
  name: data.name,
  description: data.description,
  address: {
    '@type': 'PostalAddress',
    ...data.address,
    addressCountry: 'AR'
  },
  ...(data.phone && { telephone: data.phone }),
  url: data.url || BASE_URL,
  priceRange: 'Consultar',
  areaServed: {
    '@type': 'City',
    name: data.address.addressLocality
  }
});

export default DefaultSEO;


